<script lang="ts">
	import { Editor } from "@tiptap/core";
	import { Bold } from "@tiptap/extension-bold";
	import BubbleMenu from "@tiptap/extension-bubble-menu";
	import { Document } from "@tiptap/extension-document";
	import DropCursor from "@tiptap/extension-dropcursor";
	import GapCursor from "@tiptap/extension-gapcursor";
	import { HardBreak } from "@tiptap/extension-hard-break";
	import { History } from "@tiptap/extension-history";
	import { Image, type ImageOptions } from "@tiptap/extension-image";
	import { Italic } from "@tiptap/extension-italic";
	import Link from "@tiptap/extension-link";
	import { Paragraph } from "@tiptap/extension-paragraph";
	import { Strike } from "@tiptap/extension-strike";
	import { Text } from "@tiptap/extension-text";
	import { Underline } from "@tiptap/extension-underline";
	import { CharacterCount } from "@tiptap/extensions";
	import { Frame, ToolbarButton, Tooltip } from "flowbite-svelte";
	import { onMount } from "svelte";
	import { slide } from "svelte/transition";
	import { SvelteNodeViewRenderer } from "svelte-tiptap";
	import { twMerge } from "tailwind-merge";
	import Icon from "#lib/components/Icon.svelte";
	import { DataToken } from "#lib/components/content-editors/extensions/DataToken.js";
	import Heading from "#lib/components/content-editors/extensions/Heading.js";
	import LinkEditor from "#lib/components/content-editors/extensions/LinkEditor.svelte";
	import TokenizedImage from "#lib/components/content-editors/extensions/TokenizedImage.svelte";
	import { classifySelectionRelativeToLink, getLinkRangeNearSelection } from "#lib/components/content-editors/extensions/links.js";
	import { suggestionRenderer } from "#lib/components/content-editors/extensions/suggestionRenderer.js";
	import Toolbar from "#lib/components/content-editors/toolbar/Toolbar.svelte";
	import { Logger } from "#lib/logging.js";
	import { type TokenProvider } from "#lib/tokenProvider.js";
	import type { Selection } from "prosemirror-state";

	interface Props {
		provider?: TokenProvider | undefined;
		initialContent?: string;
		disabled?: boolean;
		color?: "base" | "green" | "red";
		onInput?: (html: string, charCount?: number) => void;
		onChange?: (html: string, charCount?: number) => void;
		onFocus?: () => void;
		onBlur?: () => void;
	}

	const borderClasses = {
		base: "border-gray-300 group-has-[[contenteditable]:focus]:border-primary-500",
		green: "border-green-500",
		red: "border-red-500",
	};
	const ringClasses = {
		base: "has-[[contenteditable]:focus]:border-primary-500 has-[[contenteditable]:focus]:ring-primary-500 dark:has-[[contenteditable]:focus]:border-primary-500 dark:has-[[contenteditable]:focus]:ring-primary-500",
		green: "focus-within:ring-green-500 focus-within:border-green-500 dark:focus-within:border-green-500 dark:focus-within:ring-green-500",
		red: "focus-within:ring-red-500 focus-within:border-red-500 dark:focus-within:ring-red-500 dark:focus-within:border-red-500",
	};
	const colorClasses = {
		base: "bg-gray-50 text-gray-900 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400",
		disabled: "bg-gray-50 text-gray-900 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 opacity-50 cursor-not-allowed",
		green: "bg-green-50 text-green-900 placeholder-green-700 dark:text-green-400 dark:placeholder-green-500 dark:bg-gray-700",
		red: "bg-red-50 text-red-900 placeholder-red-700 dark:text-red-500 dark:placeholder-red-500 dark:bg-gray-700",
	};

	let { provider, initialContent = "", color = "base", disabled = false, onInput, onChange, onFocus, onBlur }: Props = $props();

	let element: HTMLDivElement | undefined = $state();
	let bubbleMenuElement: HTMLDivElement | undefined = $state();
	const editorState: { editor: Editor | null } = $state({ editor: null });

	let linkUrl = $state("");
	let linkText = $state("");

	let linkEditorMode: "preview" | "edit" | "set" | "hide" = $state("hide");
	let changed = false;

	onMount(() => {
		editorState.editor = new Editor({
			element,
			editable: !disabled,
			content: initialContent,
			extensions: [
				Heading,
				Bold,
				Strike,
				Document,
				Italic,
				Paragraph,
				Text,
				(provider
					? Image.extend({
							draggable: true,
							addOptions() {
								return {
									...this.parent?.(),
									provider,
								} as ImageOptions;
							},
							addNodeView() {
								return SvelteNodeViewRenderer(TokenizedImage);
							},
							addAttributes() {
								return { ...this.parent?.(), width: { default: null, rendered: false }, height: { default: null, rendered: false } };
							},
						})
					: Image.extend({
							draggable: true,
							addAttributes() {
								return { ...this.parent?.(), width: { default: null, rendered: false }, height: { default: null, rendered: false } };
							},
						})
				).configure({
					HTMLAttributes: { loading: "lazy", width: "600", style: "max-width: 100%; height: auto;" }, // needed to prevent tiptap from fetching a data token url,
				}),
				DropCursor,
				GapCursor,
				History,
				Link.extend({
					addKeyboardShortcuts() {
						return {
							// For Mac: Cmd + K
							"Mod-k": () => {
								return doClickLink();
							},
							// For Windows/Linux: Ctrl + K
							"Ctrl-k": () => {
								return doClickLink();
							},
						};
					},
				}).configure({
					openOnClick: false,
					enableClickSelection: true,
					defaultProtocol: "https",
					protocols: ["http", "https"],
					HTMLAttributes: {
						target: "_blank",
						rel: "noopener noreferrer",
					},
					isAllowedUri: (url, ctx) => {
						try {
							const parsedUrl = url.includes(":") ? new URL(url) : new URL(`${ctx.defaultProtocol}://${url}`);
							if (!ctx.defaultValidate(parsedUrl.href)) {
								return false;
							}
							const protocol = parsedUrl.protocol.replace(":", "");
							const allowedProtocols = ctx.protocols.map(p => (typeof p === "string" ? p : p.scheme));
							if (!allowedProtocols.includes(protocol)) {
								return false;
							}
							return true;
						} catch {
							return false;
						}
					},
					shouldAutoLink: url => {
						try {
							url.includes(":") ? new URL(url) : new URL(`https://${url}`);
							return true;
						} catch {
							return false;
						}
					},
					linkOnPaste: true,
				}),
				HardBreak,
				Underline,
				...(provider
					? [
							DataToken.configure({
								provider,
								suggestion: {
									items: ({ query }) => provider?.suggest(query).map(i => i.value),
									render: suggestionRenderer,
								},
							}),
						]
					: []),
				CharacterCount,
				BubbleMenu.configure({
					element: bubbleMenuElement,
					shouldShow: props => {
						if (props.oldState && hasSelectionChanged(props.oldState.selection, props.state.selection)) {
							// shouldShow with different selection, clearingAndHiding
							clearAndHideLinkEditor();
						}
						if (props.editor.isActive("link") && classifySelectionRelativeToLink(props.editor).kind !== "at-end") {
							linkUrl = props.editor.getAttributes("link").href;
							const linkTextRange = getLinkRangeNearSelection(props.editor);
							if (linkTextRange) linkText = props.editor.state.doc.textBetween(linkTextRange.from, linkTextRange.to);

							if (linkEditorMode === "hide") linkEditorMode = "preview";
						}
						// always return true because when setting a link the shouldShow hook doesnt get run so the link editor wont open
						// content will be hidden if linkEditorMode is "hide"
						return true;
					},
					options: {
						placement: "bottom",
						offset: 8,
						flip: true,
						onHide: handleHide,
					},
				}),
			],
			onTransaction: props => {
				if (onInput) {
					onInput(props.editor.getHTML(), props.editor.storage.characterCount.characters());
				}
				changed = true;
			},
			onFocus: () => {
				if (onFocus) onFocus();
			},

			onBlur: props => {
				if (changed) {
					if (onChange) onChange(props.editor.getHTML(), props.editor.storage.characterCount.characters());
					changed = false;
				}
				if (onBlur) onBlur();
			},
			editorProps: {
				attributes: {
					class: "prose prose-sm sm:prose-base lg:prose-lg xl:prose-2xl m-5 focus:outline-none",
				},
			},
		});
		return () => {
			editorState.editor?.destroy();
		};
	});

	function handleClickLinkTool(e: Event) {
		e.preventDefault();
		e.stopImmediatePropagation();
		doClickLink();
	}

	function doClickLink(): boolean {
		if (!editorState.editor) {
			return false;
		}
		if (editorState.editor.isActive("link")) {
			if (editorState.editor.getAttributes("link").href === null) {
				// clicked link while not set, just toggle the link off
				editorState.editor.chain().focus().unsetLink().run();
				return true;
			}
			const linkTextRange = getLinkRangeNearSelection(editorState.editor);
			if (linkTextRange) {
				linkText = editorState.editor.state.doc.textBetween(linkTextRange.from, linkTextRange.to);
			}
			linkUrl = editorState.editor.getAttributes("link").href;
			linkEditorMode = "edit";
		} else {
			const { from, to } = editorState.editor.state.selection;
			linkText = editorState.editor.state.doc.textBetween(from, to, " ");
			try {
				const urlValue = new URL(linkText);
				linkUrl = urlValue.toString();
			} catch (_) {
				// not a valid url, leave linkUrl empty
			}
			if (editorState.editor.state.selection.from === editorState.editor.state.selection.to) {
				// open the editor to add a new link and new text
				linkEditorMode = "edit";
			} else {
				// text is highlighted, show UI to just add URL
				linkEditorMode = "set";
			}
		}
		return true;
	}

	function handleSubmitLinkEdit(url: string, text: string) {
		if (!url) {
			handleRemoveLink();
			return;
		}
		try {
			const parsedUrl = url.includes(":") ? new URL(url) : new URL(`https://${url}`);
			editorState.editor?.chain().focus().extendMarkRange("link").setLink({ href: parsedUrl.toString() }).run();
			if (text) {
				editorState.editor?.chain().focus().insertContent(text).run();
			} else {
				editorState.editor?.chain().focus().insertContent(parsedUrl.toString()).run();
			}
		} catch (e) {
			Logger.error("failed to set link", { err: e });
		}
		clearAndHideLinkEditor();
	}

	function handleRemoveLink() {
		if (editorState.editor?.isActive("link")) {
			editorState.editor?.chain().focus().extendMarkRange("link").unsetLink().run();
		}
		clearAndHideLinkEditor();
	}

	export function handleCancelLinkEdit() {
		if (editorState.editor?.isActive("link") && !editorState.editor.getAttributes("link").href) {
			editorState.editor?.chain().focus().extendMarkRange("link").unsetLink().run();
		}
		clearAndHideLinkEditor();
	}

	function handleSwitchToEditLinkMode() {
		linkEditorMode = "edit";
	}

	function clearAndHideLinkEditor() {
		linkUrl = "";
		linkText = "";
		linkEditorMode = "hide";
	}

	function hasSelectionChanged(oldSelection: Selection, selection: Selection) {
		return !oldSelection.eq(selection);
	}

	function handleHide() {
		if (linkEditorMode === "hide") return;
		handleCancelLinkEdit();
	}

	function handleKeyDown(e: KeyboardEvent) {
		if (e.key === "Enter") {
			e.preventDefault();
			handleSubmitLinkEdit(linkUrl, linkText);
		}
		if (e.key === "Escape") {
			e.preventDefault();
			handleCancelLinkEdit();
			e.stopPropagation();
		}
		if (e.metaKey && e.key === "k") {
			e.stopPropagation();
		}
	}

	export function reset() {
		editorState.editor?.commands.setContent(initialContent);
	}
</script>

<div class={twMerge("max-h-screen w-full", ringClasses[color])} onkeydown={handleKeyDown} tabindex="0" role="dialog">
	{#if editorState.editor}
		<Toolbar bind:disabled editor={editorState.editor} class={borderClasses[color]} onClickLink={handleClickLinkTool} {provider} />
	{/if}
	<Frame color="gray" class={twMerge("relative overflow-y-auto rounded-br-lg rounded-bl-lg", colorClasses[disabled ? "disabled" : color], borderClasses[color])} border onclick={() => (!editorState.editor?.isFocused && linkEditorMode === "hide" ? editorState.editor?.commands.focus() : undefined)}>
		<div class="max-h-[65vh] min-h-64" bind:this={element}></div>
		<div bind:this={bubbleMenuElement}>
			{#if linkEditorMode !== "hide"}
				<div in:slide={{ duration: 200 }} class="flex min-w-64 items-center rounded-2xl bg-white p-2 shadow">
					{#if linkEditorMode === "preview"}
						<div class="ml-2 flex items-center gap-2">
							<div class="flex items-center gap-1">
								<Icon height="16px" name="link" />
								<a class="max-w-64" href={linkUrl} target="_blank" rel="external noopener noreferrer"><p class="max-w-64 overflow-x-hidden text-nowrap text-ellipsis text-blue-600 underline">{linkUrl}</p></a>
							</div>
							<ToolbarButton data-action-name="link-editor-edit" onclick={handleSwitchToEditLinkMode}><Icon height="16px" name="pencil" /></ToolbarButton>
							<Tooltip type="light">Edit</Tooltip>
							<ToolbarButton data-action-name="link-editor-delete" onclick={handleRemoveLink}><Icon height="16px" name="trash" /></ToolbarButton>
							<Tooltip type="light">Remove</Tooltip>
						</div>
					{:else if linkEditorMode === "edit"}
						<div class="flex items-center">
							<LinkEditor onRemove={handleRemoveLink} bind:linkText bind:linkUrl onSubmit={handleSubmitLinkEdit} />
						</div>
					{:else if linkEditorMode === "set"}
						<div class="flex items-center">
							<LinkEditor justSettingUrl onRemove={handleRemoveLink} bind:linkText bind:linkUrl onSubmit={handleSubmitLinkEdit} />
						</div>
					{/if}
				</div>
			{/if}
		</div>
	</Frame>
</div>

<style>
	:global(.ProseMirror.tiptap a) {
		text-decoration: underline;
		color: var(--color-blue-600);
	}
</style>
